import { defineStore } from 'pinia'

import { checkUserAccess, checkWorkspaceAccess, getLogout } from '@/api/auth-routes'

import { localSort } from '@/helpers/app-helper'
import { removeAccessToken } from '@/helpers/auth-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'

import { APP_ROLES } from '@/constants/appRoles'
import { TWO_FACTOR_AUTH_ID } from '@/constants/two-factor-auth/twoFactorAuthId'

export const useAuthorizationStore = defineStore('authorizationStore', {
    state: () => ({
        account: null,
        user: null,
        bankBanner: false,
        showLifetime: false,
        isManager: false,
        role: '',
        isHR: false,
        rates: {},
        workspace: null,
        workspaces: [],
        numbersPermissions: [],
        pinEnabled: false,
        verificationProgress: [],
        availablePaymentMethods: [],
        two_factor_enabled: false,
        two_factor_enforcement: {},
        twoFactorMethods: [],
    }),

    getters: {
        isOwner(state) {
            return !!state.account
        },

        isReporter(state) {
            return state.role === APP_ROLES.reporter
        },

        isVendor(state) {
            return state.role === APP_ROLES.vendor
        },

        isBuyer(state) {
            return state.role === APP_ROLES.buyer
        },

        isWorkspaceOwner(state) {
            return state.workspace && state.workspace.role === APP_ROLES.owner
        },

        hideTranscriptionByWorkspace(state) {
            return state.isHR
        },

        isAccountTrialEnded(state) {
            return state.account && state.account.trial_ended && !state.account.is_active
        },

        isAccountTrial(state) {
            return state.account && !state.account.trial_ended && !state.account.is_active
        },

        isAccountReview(state) {
            return state.account && state.account.in_review
        },

        isEnableUserTwoFactor(state) {
            const { two_factor_auth_id } = state.user || {}
            return two_factor_auth_id === TWO_FACTOR_AUTH_ID
        },

        isRequiredUserTwoFactor(state) {
            const { blocked } = state.two_factor_enforcement
            return blocked
        },

        isGracePeriodUserTwoFactor(state) {
            const { show_reminder_popup } = state.two_factor_enforcement
            return show_reminder_popup
        },

        getWorkspaceTimezoneId(state) {
            if (!state.workspace && !state.workspace?.timezone_id) {
                return 46
            }

            return state.workspace.timezone_id
        },

        getWorkspaceSlug(state) {
            if (!state.workspace || !state.workspace.hasOwnProperty('slug')) {
                return '_'
            }

            return state.workspace.slug
        },

        getAccountSlug(state) {
            if (!state.account || !state.account.hasOwnProperty('slug')) {
                return '_'
            }

            return state.account.slug
        },

        getUserId(state) {
            if (!state.user || !state.user.hasOwnProperty('id')) {
                return
            }

            return state.user.id
        },

        getTranscriptionRate(state) {
            return state.rates.hasOwnProperty('price_transcription') ? state.rates.price_transcription : '0.01'
        },
    },

    actions: {
        saveUserData({
            account,
            user,
            workspaces,
            bank_banner = false,
            two_factor_enabled = false,
            methods = [],
            two_factor_enforcement = {},
        }) {
            this.saveAccount(account)
            this.setVerificationProgress(account)
            this.updateUser(user)
            this.saveBankBanner(bank_banner)
            this.saveWorkspaces(workspaces)
            this.saveTwoFactor(two_factor_enabled, methods)
            this.saveTwoFactorEnforcement(two_factor_enforcement)

            if (Array.isArray(workspaces) && workspaces.length) {
                const workspacesSort = localSort(workspaces, 'number', 'last_visit', true)
                this.saveWorkspace(workspacesSort[0])
            }
        },

        saveTwoFactor(two_factor_enabled, methods) {
            this.two_factor_enabled = two_factor_enabled
            this.twoFactorMethods = methods
        },

        saveTwoFactorEnforcement(two_factor_enforcement) {
            this.two_factor_enforcement = two_factor_enforcement
        },

        setVerificationProgress(account) {
            if (account) {
                this.verificationProgress = account.verification
            }
        },

        saveAccount(account) {
            this.account = account
        },

        updateAccount(payload) {
            if (!this.account) {
                return
            }

            this.account = {
                ...this.account,
                ...payload,
            }
        },

        updateUser(user) {
            this.user = user
        },

        saveBankBanner(bankBanner) {
            this.bankBanner = bankBanner
        },

        saveWorkspaces(workspaces) {
            this.workspaces = workspaces
        },

        updateWorkspace(workspace) {
            const { slug } = workspace

            const updateWorkspaces = this.workspaces.map((el) => {
                if (el.slug === slug) {
                    return workspace
                }
                return el
            })

            this.workspace = workspace
            this.workspaces = updateWorkspaces
        },

        addWorkspace(workspace) {
            this.workspaces.push(workspace)
        },

        removeWorkspace(slug) {
            this.workspaces = this.workspaces.filter((v) => v.slug !== slug)

            if (this.workspace.slug === slug) {
                this.workspace = this.workspaces[0]
            }
        },

        saveWorkspace(workspace) {
            this.workspace = workspace
        },

        updateWorkspaceTimezoneId(id) {
            this.workspace.timezone_id = id
        },

        saveRole(role) {
            this.role = role
        },

        saveRates(rates) {
            this.rates = rates || {}
        },

        saveLifetime(value) {
            this.showLifetime = value
        },

        saveIsManager(isManager) {
            this.isManager = isManager
        },

        saveWorkSpaceSettings(payload) {
            const permissionsStore = usePermissionsStore()

            this.saveIsManager(payload.is_manager)
            this.saveHr(!!payload.hr)
            this.saveRole(payload.role)
            this.saveRates(payload.rates)
            this.saveLifetime(!!payload.has_byoc_numbers || !!payload.bank_lifetime_enabled)
            permissionsStore.setPermissions(payload)

            this.numbersPermissions = payload.hasOwnProperty('numbers_permissions') ? payload.numbers_permissions : []
            this.availablePaymentMethods = payload.hasOwnProperty('payment_methods') ? payload.payment_methods : []
            this.setPinEnabled(payload.hasOwnProperty('pin_enabled') ? payload.pin_enabled : false)
        },

        setPinEnabled(enabled) {
            this.pinEnabled = enabled
        },

        saveHr(isHR) {
            this.isHR = isHR
        },

        async getCheckWorkspace(newSlug) {
            if (!this.workspaces.length) {
                return
            }

            const foundWorkspace = this.workspaces.find((w) => w.slug === newSlug)

            const slug = foundWorkspace ? newSlug : this.workspace.slug

            if (!slug) {
                this.saveRole('Owner')

                return
            }

            const { success, payload, message } = await checkWorkspaceAccess(slug)

            if (success) {
                if (foundWorkspace) {
                    this.saveWorkspace(foundWorkspace)
                }

                this.saveWorkSpaceSettings(payload)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
                this.$reset()
            }
        },

        async getCheckUserAccess() {
            const { success, payload, message } = await checkUserAccess()

            if (success) {
                this.saveUserData(payload)
            } else {
                this.$reset()
                removeAccessToken()
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async logout() {
            const defaultStore = useDefaultStore()

            const { success, message } = await getLogout()

            if (success) {
                defaultStore.setSuccessMessage({ message })
                this.$reset()
                removeAccessToken()
            } else {
                defaultStore.setErrorMessage({ message })
            }

            return { success }
        },
    },
})
